<template>
    <div class="page-container">
        <soft-alert v-if="alertTable.state" :color="alertTable.color" class="mb-3" dismissible>
            {{ alertTable.message }}
        </soft-alert>

        <CrmPageHeader
            title="Eliminar o trasladar inmuebles"
            subtitle="Escoge un agente y elimina o pasa sus inmuebles a otro agente"
            icon="fas fa-trash-alt"
            icon-bg="linear-gradient(135deg,#7F1D1D,#DC2626)"
        />

        <!-- 1. Agente -->
        <div class="crm-card mb-4">
            <div class="crm-card__body">
                <label class="filter-label">Agente</label>
                <v-select
                    v-model="agenteSel"
                    :options="agentes"
                    :filterable="false"
                    :loading="loadingAgentes"
                    :clearable="true"
                    placeholder="Busca por correo o nombre"
                    class="agente-select"
                    @search="buscarAgentes"
                >
                    <template #option="a">
                        <div class="d-flex justify-content-between align-items-center gap-2">
                            <span>
                                {{ a.nombre }} <span class="text-secondary">— {{ a.email }}</span>
                                <span v-if="a.es_cabeza_cuenta" class="tag tag--admin ms-1">Cuenta</span>
                                <span v-if="a.estado !== 'activo'" class="tag tag--muted ms-1">{{ a.estado }}</span>
                            </span>
                            <span class="text-xs text-secondary">{{ a.total_inmuebles }} inm.</span>
                        </div>
                    </template>
                    <template #no-options="{ search }">
                        <span class="text-sm text-secondary">{{ textoSinAgentes(search) }}</span>
                    </template>
                </v-select>

                <p v-if="loadingResumen" class="text-sm text-secondary mt-3 mb-0">
                    <i class="fas fa-spinner fa-spin me-1"></i> Cargando resumen…
                </p>

                <!-- 2. Resumen -->
                <template v-if="resumen && !loadingResumen">
                    <div class="row g-3 mt-1">
                        <div class="col-sm-4">
                            <div class="stat" :class="{ 'stat--sel': !incluirHijos }">
                                <p class="stat__label">Propios</p>
                                <p class="stat__value">{{ resumen.totales.propios }}</p>
                            </div>
                        </div>
                        <div class="col-sm-4">
                            <div class="stat">
                                <p class="stat__label">De sus hijos ({{ resumen.agente.hijos_count }})</p>
                                <p class="stat__value">{{ resumen.totales.hijos }}</p>
                            </div>
                        </div>
                        <div class="col-sm-4">
                            <div class="stat" :class="{ 'stat--sel': incluirHijos }">
                                <p class="stat__label">Con hijos</p>
                                <p class="stat__value">{{ resumen.totales.incluye_hijos }}</p>
                            </div>
                        </div>
                    </div>

                    <div v-if="tieneHijos" class="form-check form-switch mt-3">
                        <input id="incluirHijos" v-model="incluirHijos" class="form-check-input" type="checkbox" />
                        <label class="form-check-label" for="incluirHijos">
                            Incluir inmuebles de los agentes hijos
                        </label>
                    </div>
                    <p v-if="resumen.agente.es_cabeza_cuenta && !incluirHijos && tieneHijos" class="hint mb-0">
                        "Propios" de la cuenta incluye los creados por importaciones o por usuarios ya borrados.
                    </p>
                </template>
            </div>
        </div>

        <!-- 3. Acción -->
        <div v-if="resumen && !loadingResumen" class="crm-card mb-4">
            <div class="crm-card__body">
                <div class="row g-3">
                    <div class="col-md-6">
                        <label class="filter-label">Qué hacer</label>
                        <div class="seg">
                            <button type="button" class="seg__btn" :class="{ 'seg__btn--danger': accion === 'eliminar' }" @click="accion = 'eliminar'">
                                <i class="fas fa-trash-alt me-1"></i> Eliminar
                            </button>
                            <button type="button" class="seg__btn" :class="{ 'seg__btn--on': accion === 'trasladar' }" @click="accion = 'trasladar'">
                                <i class="fas fa-exchange-alt me-1"></i> Trasladar a otro agente
                            </button>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <label class="filter-label">Cuáles</label>
                        <div class="seg">
                            <button type="button" class="seg__btn" :class="{ 'seg__btn--on': alcance === 'all' }" @click="alcance = 'all'">
                                Todos ({{ totalAlcance }})
                            </button>
                            <button type="button" class="seg__btn" :class="{ 'seg__btn--on': alcance === 'ids' }" @click="alcance = 'ids'">
                                Por ids
                            </button>
                        </div>
                    </div>
                </div>

                <div v-if="accion === 'eliminar'" class="warn-box mt-3">
                    <i class="fas fa-exclamation-triangle"></i>
                    <div>
                        <strong>La eliminación es definitiva.</strong>
                        No hay papelera ni forma de revertirla. Antes de borrar, el proceso despublica
                        cada inmueble de los portales y borra sus imágenes, tareas y agendas.
                    </div>
                </div>

                <div v-if="accion === 'trasladar'" class="mt-3">
                    <label class="filter-label">Trasladar a</label>
                    <v-select
                        v-model="destinoSel"
                        :options="destinos"
                        placeholder="Agente activo de la misma inmobiliaria"
                        class="agente-select"
                    />
                    <p class="hint mb-0">
                        Se re-publican en los portales donde ya estaban, con el contacto del nuevo agente.
                        No se tocan clientes, propietarios, citas ni tareas.
                    </p>
                </div>

                <div v-if="alcance === 'ids'" class="mt-3">
                    <label class="filter-label">Ids de los inmuebles</label>
                    <textarea
                        v-model="entrada"
                        class="form-control ids-input"
                        rows="5"
                        placeholder="18102, 18103, 18104 — puedes separarlos por comas, espacios o saltos de línea"
                    ></textarea>

                    <div class="d-flex flex-wrap align-items-center gap-2 mt-3">
                        <span class="chip" :class="excedeLimite ? 'chip--danger' : 'chip--ok'">
                            <i class="fas fa-hashtag me-1"></i>
                            {{ ids.length }} id(s) válidos
                        </span>
                        <span v-if="duplicados > 0" class="chip chip--muted">
                            {{ duplicados }} repetido(s) descartado(s)
                        </span>
                        <span v-if="invalidos.length" class="chip chip--warn">
                            Ignorado en el texto: {{ invalidos.slice(0, 5).join(', ') }}<span v-if="invalidos.length > 5">…</span>
                        </span>
                        <span v-if="excedeLimite" class="chip chip--danger">
                            Máximo {{ maxIds }} por solicitud — usa "Todos" o envía por partes
                        </span>
                    </div>
                    <p class="hint mb-0">Solo se procesan los ids que sean de este agente{{ incluirHijos ? ' o de sus hijos' : '' }}; el resto se ignora.</p>
                </div>

                <div class="d-flex gap-2 mt-4">
                    <soft-button
                        variant="gradient"
                        :color="accion === 'eliminar' ? 'danger' : 'info'"
                        size="sm"
                        :disabled="!puedeEnviar"
                        :loading="spinner"
                        @click="abrirConfirmacion"
                    >
                        <template v-if="accion === 'eliminar'">
                            <i class="fas fa-trash-alt me-1"></i> Eliminar {{ cantidad }} inmueble(s)
                        </template>
                        <template v-else>
                            <i class="fas fa-exchange-alt me-1"></i> Trasladar {{ cantidad }} inmueble(s)
                        </template>
                    </soft-button>
                    <soft-button variant="outline" color="secondary" size="sm" @click="limpiar">
                        Limpiar
                    </soft-button>
                </div>
            </div>
        </div>

        <!-- Resultado del último envío -->
        <div v-if="resultado" class="crm-card">
            <div class="crm-card__body">
                <h6 class="result-title">
                    {{ resultado.accion === 'eliminar' ? 'Resultado de la eliminación' : 'Resultado del traslado' }}
                </h6>

                <div v-if="resultado.accion === 'eliminar'" class="row g-3 mb-3">
                    <div class="col-sm-4">
                        <div class="stat">
                            <p class="stat__label">Recibidos</p>
                            <p class="stat__value">{{ resultado.total_recibidos != null ? resultado.total_recibidos : 'Todos' }}</p>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="stat stat--ok">
                            <p class="stat__label">En proceso</p>
                            <p class="stat__value">{{ resultado.total_en_proceso }}</p>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="stat stat--warn">
                            <p class="stat__label">Ignorados</p>
                            <p class="stat__value">{{ (resultado.ids_ignorados || []).length }}</p>
                        </div>
                    </div>
                </div>

                <div v-else class="row g-3 mb-3">
                    <div class="col-sm-4">
                        <div class="stat stat--ok">
                            <p class="stat__label">Trasladados</p>
                            <p class="stat__value">{{ resultado.total_trasladados }}</p>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="stat">
                            <p class="stat__label">Ya eran del destino</p>
                            <p class="stat__value">{{ resultado.total_ya_del_destino || 0 }}</p>
                        </div>
                    </div>
                    <div class="col-sm-4">
                        <div class="stat stat--warn">
                            <p class="stat__label">Ignorados</p>
                            <p class="stat__value">{{ (resultado.ids_ignorados || []).length }}</p>
                        </div>
                    </div>
                </div>

                <p v-if="resultado.accion === 'eliminar' && resultado.total_en_proceso" class="text-sm text-secondary mb-3">
                    <i class="fas fa-clock me-1"></i>
                    El borrado es asíncrono: la solicitud quedó encolada y un worker la procesa.
                    Revisa el listado de inmuebles en unos minutos para confirmar.
                </p>
                <p v-if="resultado.accion === 'trasladar' && resultado.destino" class="text-sm text-secondary mb-3">
                    <i class="fas fa-check me-1"></i>
                    Ahora son de <strong>{{ resultado.destino.email }}</strong>.
                    <span v-if="resultado.lote">Lote: <code>{{ resultado.lote }}</code></span>
                </p>

                <div v-if="(resultado.ids_en_proceso || resultado.ids_trasladados || []).length" class="mb-3">
                    <p class="list-label">{{ resultado.accion === 'eliminar' ? 'Se van a eliminar' : 'Trasladados' }}</p>
                    <p class="id-list">{{ (resultado.ids_en_proceso || resultado.ids_trasladados).join(', ') }}</p>
                </div>

                <div v-if="(resultado.ids_ignorados || []).length">
                    <div class="d-flex align-items-center justify-content-between">
                        <p class="list-label mb-0">No existen o no son de este agente</p>
                        <button type="button" class="copy-btn" @click="copiarIgnorados">
                            <i class="fas fa-copy me-1"></i> Copiar
                        </button>
                    </div>
                    <p class="id-list id-list--warn">{{ resultado.ids_ignorados.join(', ') }}</p>
                </div>
            </div>
        </div>

        <!-- Confirmación -->
        <soft-modal :modal-active="confirmActive" size="sm" @close="cerrarConfirmacion">
            <template #header>
                <span v-if="accion === 'eliminar'" class="text-danger">
                    <i class="fas fa-exclamation-triangle me-2"></i>Confirmar eliminación
                </span>
                <span v-else><i class="fas fa-exchange-alt me-2"></i>Confirmar traslado</span>
            </template>

            <template v-if="agenteSel">
                <p v-if="accion === 'eliminar'" class="mb-2">
                    Vas a eliminar <strong>{{ alcance === 'all' ? 'todos los' : 'hasta' }} {{ cantidad }} inmueble(s)</strong>
                    de <strong>{{ agenteSel.email }}</strong>{{ incluirHijos ? ' y sus agentes hijos' : '' }}
                    de forma <strong>definitiva</strong>.
                </p>
                <p v-else class="mb-2">
                    Vas a pasar <strong>{{ alcance === 'all' ? 'todos los' : 'hasta' }} {{ cantidad }} inmueble(s)</strong>
                    de <strong>{{ agenteSel.email }}</strong>{{ incluirHijos ? ' y sus agentes hijos' : '' }}
                    a <strong>{{ destinoSel?.email }}</strong>.
                </p>
                <p v-if="accion === 'eliminar'" class="text-sm text-secondary mb-2">
                    Se despublicarán de los portales y se borrarán sus imágenes, tareas y agendas.
                    Esta acción no se puede deshacer.
                </p>
                <div v-if="requiereTeclear">
                    <label class="filter-label">Escribe <strong>{{ cantidad }}</strong> para confirmar</label>
                    <input v-model="confirmacionTexto" type="text" inputmode="numeric" class="form-control form-control-sm" />
                </div>
            </template>

            <template #footer>
                <soft-button variant="outline" color="secondary" size="sm" @click="cerrarConfirmacion">
                    Cancelar
                </soft-button>
                <soft-button
                    variant="gradient"
                    :color="accion === 'eliminar' ? 'danger' : 'info'"
                    size="sm"
                    :disabled="!confirmacionValida"
                    @click="ejecutar"
                >
                    Sí, {{ accion === 'eliminar' ? 'eliminar' : 'trasladar' }} {{ cantidad }}
                </soft-button>
            </template>
        </soft-modal>
    </div>
</template>

<script>
import SoftButton from "@/components/SoftButton.vue";
import SoftAlert from "@/components/SoftAlert.vue";
import SoftModal from "@/components/SoftModal.vue";
import CrmPageHeader from "@/components/CrmPageHeader.vue";
import { useEliminacionMasiva } from "./useEliminacionMasiva.js";

export default {
    name: "EliminacionMasivaInmuebles",
    components: {
        SoftButton,
        SoftAlert,
        SoftModal,
        CrmPageHeader,
    },
    setup() {
        return { ...useEliminacionMasiva() };
    },
};
</script>

<style scoped>
.warn-box {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 14px 16px;
    border-radius: 12px;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    color: #7F1D1D;
    font-size: 0.82rem;
    line-height: 1.45;
}

.warn-box i {
    margin-top: 2px;
}

.hint {
    font-size: 0.74rem;
    color: #64748B;
    margin-top: 6px;
}

.tag {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 6px;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
}

.tag--admin { background: #EFF6FF; color: #1D4ED8; }
.tag--muted { background: #F1F5F9; color: #64748B; }

.seg {
    display: inline-flex;
    flex-wrap: wrap;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    overflow: hidden;
}

.seg__btn {
    border: none;
    background: #fff;
    padding: 8px 14px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
}

.seg__btn + .seg__btn { border-left: 1px solid #E2E8F0; }
.seg__btn--on     { background: #EFF6FF; color: #1D4ED8; }
.seg__btn--danger { background: #FEF2F2; color: #B91C1C; }

.ids-input {
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 0.85rem;
    resize: vertical;
}

.chip {
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 0.74rem;
    font-weight: 600;
    background: #F1F5F9;
    color: #475569;
}

.chip--ok     { background: #ECFDF5; color: #047857; }
.chip--warn   { background: #FFFBEB; color: #B45309; }
.chip--danger { background: #FEF2F2; color: #B91C1C; }
.chip--muted  { background: #F1F5F9; color: #64748B; }

.result-title {
    font-size: 0.9rem;
    font-weight: 700;
    color: #1E293B;
    margin-bottom: 14px;
}

.stat {
    padding: 12px 14px;
    border-radius: 12px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
}

.stat--ok   { background: #ECFDF5; border-color: #A7F3D0; }
.stat--warn { background: #FFFBEB; border-color: #FDE68A; }
.stat--sel  { background: #EFF6FF; border-color: #93C5FD; }

.stat__label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748B;
    margin: 0 0 4px;
}

.stat__value {
    font-size: 1.35rem;
    font-weight: 700;
    color: #1E293B;
    margin: 0;
}

.list-label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748B;
    margin-bottom: 6px;
}

.id-list {
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 0.78rem;
    color: #334155;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    padding: 10px 12px;
    margin: 0;
    max-height: 140px;
    overflow-y: auto;
    word-break: break-word;
}

.id-list--warn {
    background: #FFFBEB;
    border-color: #FDE68A;
    color: #92400E;
}

.copy-btn {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.74rem;
    font-weight: 600;
    color: #2563EB;
    cursor: pointer;
}
</style>
